<template>
  <div class="mgx_key_group_container">
    <div class="device_switch">
      <div
        v-for="action in quickSelectActions"
        :key="action.id"
        class="item quick_action"
        :class="{ selected: isQuickActionSelected(action.id), compact: action.compact }"
        @click="handleQuickSelect(action.id)"
        @mouseenter="handleQuickActionMouseEnter(action.id)"
        @mouseleave="clearHoverKeys()"
      >
        {{ $t(action.labelKey) }}
      </div>

      <template v-if="isThereGroup">
        <div class="device_switch_separator"></div>
        <template v-for="[key, value] in visibleKeyGroups" :key="key">
          <div class="item" :class="{ selected: key == magnetSetKeyGroup.state.keysGroupType }" @click="selectKeyGroup(key)" @mouseenter="handleMouseEnter(key)" @mouseleave="clearHoverKeys()">
            <input class="selector" type="checkbox" :checked="key == magnetSetKeyGroup.state.keysGroupType" />
            {{ $t(value?.groupNameTranslationKey || '') }}
            <span v-if="magnetSetKeyGroup.state.defaultKeysGroup == key && !specialCheckCurrent(speciaBehiver.Device_UnknownAxis)"> -{{ $t('magnet_original') }} </span>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { KK, VK, getKKByVK } from '@/define/define'
import { KeyGroupInfoMap } from '@/define/key_group'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'
import { getCurrentDeviceMagnetKKs } from '@/profiles/selector'
import { magnetSetKeyGroup } from '@/service/driver/magnet_key_group'
import { magnetSet } from '@/service/driver/magnet_set'
import { KeyGroup } from '@libs/types/enum'

import { computed } from 'vue'

type QuickSelectId = 'all' | 'invert' | 'wasd' | 'arrow' | 'numberRow' | 'alphaZone'

interface QuickSelectAction {
  id: QuickSelectId
  labelKey: string
  compact?: boolean
}

const quickSelectActions: QuickSelectAction[] = [
  { id: 'all', labelKey: 'all_keys_selected' },
  { id: 'invert', labelKey: 'magnet_quick_select_invert' },
  { id: 'wasd', labelKey: 'magnet_quick_select_wasd', compact: true },
  { id: 'arrow', labelKey: 'magnet_quick_select_arrow' },
  { id: 'numberRow', labelKey: 'magnet_quick_select_number_row' },
  { id: 'alphaZone', labelKey: 'magnet_quick_select_alpha_zone' },
]

const quickSelectVKMap: Record<Exclude<QuickSelectId, 'all' | 'invert'>, VK[]> = {
  wasd: [0x57, 0x41, 0x53, 0x44],
  arrow: [0x26, 0x25, 0x28, 0x27],
  numberRow: [0x31, 0x32, 0x33, 0x34, 0x35, 0x36, 0x37, 0x38, 0x39, 0x30],
  alphaZone: [0x51, 0x57, 0x45, 0x52, 0x54, 0x59, 0x55, 0x49, 0x4f, 0x50, 0x41, 0x53, 0x44, 0x46, 0x47, 0x48, 0x4a, 0x4b, 0x4c, 0x5a, 0x58, 0x43, 0x56, 0x42, 0x4e, 0x4d],
}

const isSelectedAllKeys = computed(() => {
  return areSameKeys(magnetSet.selectedKeys.value, getSelectableMagnetKeys())
})

const sortedKeyGroups = computed(() => {
  const map = KeyGroupInfoMap()
  const defaultGroup = magnetSetKeyGroup.state.defaultKeysGroup

  return Array.from(map.entries()).sort((a, b) => {
    if (a[0] === defaultGroup) return -1
    if (b[0] === defaultGroup) return 1
    return 0
  })
})

const visibleKeyGroups = computed(() => {
  return sortedKeyGroups.value.filter(([key]) => {
    return (magnetSetKeyGroup.state.keysGroup.get(key) || []).length > 0
  })
})

const getSelectableMagnetKeys = (): KK[] => {
  return getCurrentDeviceMagnetKKs.value.map((value) => parseInt(value.toString(), 10))
}

const dedupeKeys = (keys: KK[]): KK[] => {
  return Array.from(new Set(keys))
}

const areSameKeys = (source: KK[], target: KK[]): boolean => {
  if (source.length !== target.length) {
    return false
  }

  const sourceSet = new Set(source)
  return target.every((key) => sourceSet.has(key))
}

const resolveQuickSelectKeys = (id: QuickSelectId): KK[] => {
  const selectableKeys = getSelectableMagnetKeys()

  if (id === 'all') {
    return selectableKeys
  }

  if (id === 'invert') {
    const selectedSet = new Set(magnetSet.selectedKeys.value)
    return selectableKeys.filter((key) => !selectedSet.has(key))
  }

  const keys = quickSelectVKMap[id].map((vk) => getKKByVK(vk)).filter((kk): kk is KK => kk !== null)

  const selectableSet = new Set(selectableKeys)
  return dedupeKeys(keys).filter((key) => selectableSet.has(key))
}

const applySelection = (keys: KK[]) => {
  const normalizedKeys = dedupeKeys(keys)
  magnetSet.selectedKeys.value = normalizedKeys
  magnetSetKeyGroup.state.keysGroupType = magnetSetKeyGroup.getExactMatchedGroup(normalizedKeys)
  clearHoverKeys()
}

const isQuickActionSelected = (id: QuickSelectId): boolean => {
  if (id === 'invert') {
    return false
  }

  return areSameKeys(magnetSet.selectedKeys.value, resolveQuickSelectKeys(id))
}

const handleQuickSelect = (id: QuickSelectId) => {
  if (id === 'all' && isSelectedAllKeys.value) {
    applySelection([])
    return
  }

  applySelection(resolveQuickSelectKeys(id))
}

const selectKeyGroup = (item: KeyGroup) => {
  if (magnetSetKeyGroup.state.keysGroupType == item) {
    applySelection([])
    return
  }

  const kks = magnetSetKeyGroup.state.keysGroup.get(item) || []
  applySelection([...kks])
}

const isThereGroup = computed(() => {
  for (const [key, values] of magnetSetKeyGroup.state.keysGroup) {
    if (values.length > 0 && key != magnetSetKeyGroup.state.defaultKeysGroup) {
      return true
    }
  }
  return false
})

const handleQuickActionMouseEnter = (id: QuickSelectId) => {
  magnetSetKeyGroup.state.hoverKeys = resolveQuickSelectKeys(id)
}

const clearHoverKeys = () => {
  magnetSetKeyGroup.state.hoverKeys.length = 0
}

const handleMouseEnter = (item: KeyGroup) => {
  const kks = magnetSetKeyGroup.state.keysGroup.get(item) || []
  magnetSetKeyGroup.state.hoverKeys = [...kks]
}
</script>

<style lang="less" scoped>
.mgx_key_group_container {
  margin-bottom: 0;
  width: max-content;
  max-width: 100%;
}

.device_switch {
  display: flex;
  align-items: center;
  color: var(--color-dark);
  flex-wrap: wrap;
  gap: 8px;

  .device_switch_separator {
    width: 1px;
    height: 28px;
    background: rgba(255, 255, 255, 0.6);
  }

  .item {
    cursor: pointer;
    font-size: 13px;
    background: rgba(255, 255, 255, 0.45);
    backdrop-filter: blur(16px);
    padding: 8px 16px;
    color: rgba(34, 34, 34, 0.66);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius-m);
    text-wrap: nowrap;
    transition: var(--transition-base);
    border: 1px solid rgba(255, 255, 255, 0.5);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
    font-weight: 800;

    &.quick_action {
      padding: 8px 14px;

      &.compact {
        min-width: 58px;
      }
    }

    &.selected {
      background: rgba(255, 255, 255, 0.94);
      color: var(--brand-color-pure);
      border-color: rgba(255, 255, 255, 0.9);
      font-weight: 900;
      box-shadow: 0 8px 20px rgba(var(--color-brand-1-rgb), 0.12);
      transform: translateY(-2px);
    }

    &:hover:not(.selected) {
      background: rgba(255, 255, 255, 0.62);
      color: var(--color-dark);
      border-color: rgba(255, 255, 255, 0.42);
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
    }

    .selector {
      display: none;
    }
  }
}
</style>
